import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useUserCourse } from '../../contexts/UserCourseContext';

function Breadcrumbs() {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseName } = useUserCourse();

  const getClassroomDetailsPath = (classroomId, tab = 'assignments') =>
    `/user/AllClassroom/classroomDetails/${classroomId}?active-tab=${tab}`;

  // Filter out empty strings and 'user'
  const pathnames = location.pathname
    .split('/')
    .filter((x) => x !== '' && x !== 'user');

  const handleBack = () => {
    navigate(-1);
  };

  // Create a mapping of path segments to readable names and icons
  const pathMap = {
    'dashboard': {
      name: 'Dashboard',
      icon: 'fluent:home-24-regular'
    },
    'profile': {
      name: 'Profile',
      icon: 'fluent:person-24-regular'
    },
    'courses': {
      name: 'Courses',
      icon: 'fluent:book-24-regular'
    },
    'feed': {
      name: 'Feed',
      icon: 'fluent:document-24-regular'
    },
    'my-feed': {
      name: 'My Feed',
      icon: 'fluent:document-24-regular'
    },
    'courseDetails': {
      name: 'Course Details',
      icon: 'fluent:book-information-24-regular'
    },
    'WatchCourse': {
      name: 'Course Content',
      icon: 'fluent:book-open-24-regular'
    },
    'orderDetails': {
      name: 'Order Details',
      icon: 'fluent:receipt-24-regular'
    },
    'orderDetailsWithPayu': {
      name: 'Order Details',
      icon: 'fluent:receipt-24-regular'
    },
    'AllClassroom': {
      name: 'Classrooms',
      icon: 'fluent:class-24-regular'
    },
    'certificates': {
      name: 'Certificates',
      icon: 'fluent:certificate-24-regular'
    },
    'notifications': {
      name: 'Notifications',
      icon: 'fluent:alert-24-regular'
    },
    'notificationDetails': {
      name: 'Notification Details',
      icon: 'fluent:alert-urgent-24-regular'
    },
    'settings': {
      name: 'Settings',
      icon: 'fluent:settings-24-regular'
    },
    'help': {
      name: 'Help',
      icon: 'fluent:question-circle-24-regular'
    },
    'faq': {
      name: 'FAQ',
      icon: 'fluent:book-question-mark-24-regular'
    },
    'ticket': {
      name: 'Tickets',
      icon: 'fluent:ticket-24-regular'
    },
    'classroomDetails': {
      name: 'Classroom Dashboard',
      icon: 'fluent:classroom-24-regular'
    },
    'assignmentsQuestions': {
      name: 'Assignment',
      icon: 'fluent:task-list-24-regular'
    },
    'assignment-quiz': {
      name: 'Assignment Quiz',
      icon: 'fluent:clipboard-task-list-24-regular'
    }
  };

  const getPathInfo = (path) => {
    const isEncodedSegment = (segment) => segment && (segment.includes('%') || segment.includes('eyJ'));

    // Handle encoded IDs
    if (isEncodedSegment(path)) {
      let parentIndex = pathnames.indexOf(path) - 1;

      while (parentIndex >= 0 && isEncodedSegment(pathnames[parentIndex])) {
        parentIndex -= 1;
      }

      const pathType = parentIndex >= 0 ? pathnames[parentIndex] : null;

      switch (pathType) {
        case 'courses':
        case 'courseDetails':
          return {
            name: 'Course Details',
            icon: 'fluent:book-information-24-regular'
          };
        case 'WatchCourse':
          return {
            name: 'Course Content',
            icon: 'fluent:book-open-24-regular'
          };
        case 'AllClassroom':
        case 'classroomDetails':
          return {
            name: 'Classroom Dashboard',
            icon: 'fluent:classroom-24-regular'
          };
        case 'assignmentsQuestions':
          return {
            name: 'Assignment',
            icon: 'fluent:task-list-24-regular'
          };
        case 'assignment-quiz':
          return {
            name: 'Assignment Quiz',
            icon: 'fluent:clipboard-task-list-24-regular'
          };
        case 'notificationDetails':
          return {
            name: 'Notification Details',
            icon: 'fluent:alert-urgent-24-regular'
          };
        default:
          return {
            name: path,
            icon: 'fluent:document-24-regular'
          };
      }
    }

    return pathMap[path] || {
      name: path.charAt(0).toUpperCase() + path.slice(1),
      icon: 'fluent:document-24-regular'
    };
  };

  // Get the current page title
  const getPageTitle = () => {
    if (pathnames.length === 0) return 'Dashboard';

    const lastSegment = pathnames[pathnames.length - 1];
    const secondLastSegment = pathnames[pathnames.length - 2];

    // Special cases for different paths
    if (pathnames.includes('assignment-quiz')) {
      return 'Assignment Quiz';
    } else if (pathnames.includes('assignmentsQuestions')) {
      return 'Assignment';
    } else if (pathnames.includes('classroomDetails')) {
      return 'Classroom Dashboard';
    } else if (secondLastSegment === 'AllClassroom') {
      return 'Classroom Dashboard';
    } else if (secondLastSegment === 'WatchCourse') {
      return courseName || 'Course Content';
    } else if (pathnames.includes('courseDetails') ||
        (secondLastSegment === 'courses' && (lastSegment.includes('%') || lastSegment.includes('eyJ')))) {
      return courseName || 'Course Details';
    } else if (lastSegment === 'notificationDetails' ||
              (secondLastSegment === 'notifications' && (lastSegment.includes('%') || lastSegment.includes('eyJ')))) {
      return 'Notification Details';
    } else if (secondLastSegment === 'help') {
      if (lastSegment === 'faq') return 'FAQ';
      if (lastSegment === 'ticket') return 'Tickets';
    } else if (lastSegment === 'orderDetails') {
      return 'Order Details';
    } else if (lastSegment === 'orderDetailsWithPayu') {
      return 'Order Details';
    }

    return getPathInfo(lastSegment).name;
  };

  const getPageIcon = () => {
    if (pathnames.length === 0) {
      return pathMap.dashboard.icon;
    }

    if (pathnames.includes('classroomDetails')) {
      return 'fluent:classroom-24-regular';
    }

    const lastSegment = pathnames[pathnames.length - 1];
    return getPathInfo(lastSegment).icon;
  };

  // Get breadcrumb items based on current path
  const getBreadcrumbItems = () => {
    const items = [
      {
        name: 'Dashboard',
        icon: 'fluent:home-24-regular',
        path: '/user/dashboard'
      }
    ];

    if (pathnames.length === 1 && pathnames[0] === 'dashboard') {
      return items;
    }

    // Get classroom ID from query params if it exists
    const params = new URLSearchParams(location.search);
    const classroomId = params.get('classroomid');

    // Special handling for assignments questions with classroom context
    if (pathnames.includes('assignmentsQuestions') && classroomId) {
      return [
        ...items,
        {
          name: 'Classrooms',
          icon: 'fluent:class-24-regular',
          path: '/user/AllClassroom'
        },
        {
          name: 'Classroom Dashboard',
          icon: 'fluent:classroom-24-regular',
          path: getClassroomDetailsPath(classroomId, 'assignments')
        },
        {
          name: 'Assignment',
          icon: 'fluent:task-list-24-regular',
          path: location.pathname + location.search
        }
      ];
    }

    if (pathnames.includes('assignment-quiz')) {
      const assignmentIndex = pathnames.indexOf('assignment-quiz');
      const encodedAssignment = pathnames[assignmentIndex + 1];
      const params = new URLSearchParams(location.search);
      const classroomId = params.get('classroomid');

      const assignmentPath = encodedAssignment
        ? `/user/assignmentsQuestions/${encodedAssignment}${classroomId ? `?classroomid=${classroomId}` : ''}`
        : '/user/assignmentsQuestions';

      const classroomDashboardPath = classroomId
        ? getClassroomDetailsPath(classroomId, 'assignments')
        : '/user/AllClassroom';

      return [
        ...items,
        {
          name: 'Classrooms',
          icon: 'fluent:class-24-regular',
          path: '/user/AllClassroom'
        },
        {
          name: 'Classroom Dashboard',
          icon: 'fluent:classroom-24-regular',
          path: classroomDashboardPath
        },
        {
          name: 'Assignment',
          icon: 'fluent:task-list-24-regular',
          path: assignmentPath
        },
        {
          name: 'Assignment Quiz',
          icon: 'fluent:clipboard-task-list-24-regular',
          path: location.pathname + location.search
        }
      ];
    }

    if (pathnames.includes('assessmentQuiz')) {
      const params = new URLSearchParams(location.search);
      const classroomId = params.get('classroomid');

      const classroomDashboardPath = classroomId
        ? getClassroomDetailsPath(classroomId, 'assessment')
        : '/user/AllClassroom';

      return [
        ...items,
        {
          name: 'Classrooms',
          icon: 'fluent:class-24-regular',
          path: '/user/AllClassroom'
        },
        {
          name: 'Classroom Dashboard',
          icon: 'fluent:classroom-24-regular',
          path: classroomDashboardPath
        },
        {
          name: 'Assessment Quiz',
          icon: 'fluent:quiz-new-24-regular',
          path: location.pathname + location.search
        }
      ];
    }

    // Special handling for classroom details
    if (pathnames.includes('classroomDetails') ||
        (pathnames.includes('AllClassroom') &&
         (pathnames[pathnames.length - 1].includes('%') || pathnames[pathnames.length - 1].includes('eyJ')))) {
      return [
        ...items,
        {
          name: 'Classrooms',
          icon: 'fluent:class-24-regular',
          path: '/user/AllClassroom'
        },
        {
          name: 'Classroom Dashboard',
          icon: 'fluent:classroom-24-regular',
          path: location.pathname + location.search
        }
      ];
    }

    // Special handling for payment success
    if (pathnames.includes('successPayUPayment')) {
      return [
        {
          name: 'Payment Successful',
          icon: 'fluent:checkmark-circle-24-regular',
          path: location.pathname + location.search
        }
      ];
    }

    // Special handling for order details
    if (pathnames.includes('orderDetails')) {
      return [
        ...items,
        {
          name: 'Courses',
          icon: 'fluent:book-24-regular',
          path: '/user/courses'
        },
        {
          name: 'Order Details',
          icon: 'fluent:receipt-24-regular',
          path: location.pathname
        }
      ];
    }

    // Special handling for order details with PayU
    if (pathnames.includes('orderDetailsWithPayu')) {
      return [
        ...items,
        {
          name: 'Courses',
          icon: 'fluent:book-24-regular',
          path: '/user/courses'
        },
        {
          name: 'Order Details',
          icon: 'fluent:receipt-24-regular',
          path: location.pathname
        }
      ];
    }

    // Special handling for help pages
    if (pathnames.includes('help')) {
      const helpItems = [
        {
          name: 'Help',
          icon: 'fluent:question-circle-24-regular',
          path: '/user/help'
        }
      ];

      // Add FAQ or Ticket based on the path
      if (pathnames.includes('faq')) {
        helpItems.push({
          name: 'FAQ',
          icon: 'fluent:book-question-mark-24-regular',
          path: location.pathname
        });
      } else if (pathnames.includes('ticket')) {
        helpItems.push({
          name: 'Tickets',
          icon: 'fluent:ticket-24-regular',
          path: location.pathname
        });
      }

      return [...items, ...helpItems];
    }

    // Special handling for notification details
    if (pathnames.includes('notificationDetails') ||
        (pathnames.includes('notifications') && pathnames[pathnames.length - 1].includes('%'))) {
      return [
        ...items,
        {
          name: 'Notifications',
          icon: 'fluent:alert-24-regular',
          path: '/user/notifications'
        },
        {
          name: 'Notification Details',
          icon: 'fluent:alert-urgent-24-regular',
          path: location.pathname
        }
      ];
    }

    // Special handling for my-feed
    if (pathnames.includes('my-feed')) {
      return [
        ...items,
        {
          name: 'Feed',
          icon: 'fluent:document-24-regular',
          path: '/user/feed'
        },
        {
          name: 'My Feed',
          icon: 'fluent:document-24-regular',
          path: location.pathname
        }
      ];
    }

    // Special handling for WatchCourse
    if (pathnames.includes('WatchCourse')) {
      return [
        ...items,
        {
          name: 'Courses',
          icon: 'fluent:book-24-regular',
          path: '/user/courses'
        },
        {
          name: courseName || 'Course Content',
          icon: 'fluent:book-open-24-regular',
          path: location.pathname
        }
      ];
    }

    // Special handling for course details
    if (pathnames.includes('courseDetails') ||
        (pathnames.includes('courses') &&
         (pathnames[pathnames.length - 1].includes('%') || pathnames[pathnames.length - 1].includes('eyJ')))) {
      return [
        ...items,
        {
          name: 'Courses',
          icon: 'fluent:book-24-regular',
          path: '/user/courses'
        },
        {
          name: courseName || 'Course Details',
          icon: 'fluent:book-information-24-regular',
          path: location.pathname
        }
      ];
    }

    // Add current path item
    if (pathnames.length > 0) {
      const currentPath = pathnames[pathnames.length - 1];
      const pathInfo = getPathInfo(currentPath);

      items.push({
        name: pathInfo.name,
        icon: pathInfo.icon,
        path: location.pathname
      });
    }

    return items;
  };

  const breadcrumbItems = getBreadcrumbItems();

  return (
    <nav aria-label="breadcrumb" className="tw-bg-white tw-py-4 tw-px-6 tw-mb-4 tw-rounded-xl tw-shadow-sm">
      <div className="tw-flex tw-justify-between tw-items-center">
        {/* Page Title with Back Button */}
        <div className="tw-flex tw-min-w-0 tw-items-center">
          {pathnames.length > 1 && !pathnames.includes('successPayUPayment') && (
            <button
              onClick={handleBack}
              className={"[[data-theme='dark']_&:hover]:tw-bg-[var(--bg-tertiary)] [&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button btn-icon ui-button-light tw-rounded-full tw-me-2"}
              style={{
                width: '32px',
                height: '32px',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #e0e0e0'
              }}
            >
              <Icon icon="fluent:arrow-left-24-regular" width="20" height="20" />
            </button>
          )}
          <div className="tw-flex tw-min-w-0 tw-items-center">
            <Icon
              icon={getPageIcon()}
              className="tw-me-2 tw-shrink-0"
              width="24"
              height="24"
            />
            <div className="tw-min-w-0">
              <h4 className="tw-mb-0 tw-truncate tw-font-bold" title={getPageTitle()}>
                {getPageTitle()}
              </h4>
              {courseName && pathnames.includes('WatchCourse') && (
                <p className="tw-mb-0 tw-text-xs tw-font-medium tw-text-slate-500">Course Content</p>
              )}
              {courseName && pathnames.includes('courseDetails') && (
                <p className="tw-mb-0 tw-text-xs tw-font-medium tw-text-slate-500">Course Details</p>
              )}
            </div>
          </div>
        </div>

        {/* Breadcrumbs on the right */}
        <ol className="tw-m-0 tw-p-0 breadcrumb tw-mb-0 tw-items-center tw-hidden md:tw-flex">
          {breadcrumbItems.map((item, index) => (
            <React.Fragment key={`${item.path}-${index}`}>
              {index > 0 && (
                <li className="!tw-text-[var(--secondary-color)] tw-mx-1 tw-text-slate-500">
                  <Icon icon="fluent:chevron-right-24-regular" width="16" height="16" />
                </li>
              )}
              <li className={`tw-flex tw-items-center breadcrumb-item   ${index === breadcrumbItems.length - 1 ? "![font-weight:var(--font-weight-semibold)] active tw-font-semibold" : ""}`}>
                {index === breadcrumbItems.length - 1 ? (
                    <span className="!tw-text-[var(--primary-color)] tw-text-brand-600 tw-flex tw-items-center">
                    <Icon icon={item.icon} className="tw-me-1" width="18" height="18" />
                    {item.name}
                    </span>
                ) : (
                    <Link
                    to={item.path}
                      className="!tw-text-[var(--secondary-color)] tw-no-underline tw-text-slate-500 tw-flex tw-items-center"
                    >
                    <Icon icon={item.icon} className="tw-me-1" width="18" height="18" />
                    {item.name}
                    </Link>
                  )}
                  </li>
              </React.Fragment>
          ))}
        </ol>
      </div>
    </nav>
  );
}

export default Breadcrumbs;
